/**
 * Canonical post-login destination resolver.
 *
 * Single source of truth for "where does this authenticated user land?".
 * Used by usePostLoginRedirect (hook) and LocaleHomeEntry (root route).
 * Pure — no React, no supabase calls. Given capabilities + context, returns
 * a path.
 *
 * Priority ladder (from Trade Account audit remediation):
 *   1. Unverified email               → /verify-email
 *   2. Pending invitation(s)          → /trade/invitations/review
 *   3. Open claim (no trade yet)      → /trade/claim/status/:id
 *   4. Trade setup incomplete         → /trade/join/organisation
 *   5. Multiple active organisations  → /trade/select-organisation
 *   6. Dual context                   → last-used domain
 *   7. Trade-only                     → /trade/dashboard
 *   8. Consumer-only                  → /app/home
 *   9. Staff-only                     → /admin
 *  10. Fallback                       → /account/setup
 */
import type { AccountCapabilities } from "@/lib/account-capabilities";
import type { Domain } from "@/hooks/usePostLoginRedirect";

export interface ResolveLandingOptions {
  lastDomain: Domain;
  /** Optional loop-guard predicate — return true if candidate has been hit too often. */
  wouldLoop?: (candidate: string) => boolean;
}

const LOOP_SAFE = "/account/setup";

export function resolveLandingRoute(
  capabilities: AccountCapabilities,
  opts: ResolveLandingOptions,
): string {
  const { lastDomain, wouldLoop } = opts;
  const pick = (candidate: string): string =>
    wouldLoop && wouldLoop(candidate) ? LOOP_SAFE : candidate;

  if (!capabilities.userId) return "/app/home";

  // 1. Unverified identity
  if (!capabilities.emailVerified) return pick("/verify-email");

  // 2. Pending invitations
  if (capabilities.pendingInvitations.length >= 1) {
    return pick("/trade/invitations/review");
  }

  // 3. Open business claim awaiting review
  const claim = capabilities.latestActiveClaim;
  if (
    claim &&
    ["submitted", "pending", "more_info"].includes(claim.status) &&
    capabilities.tradeMemberships.length === 0
  ) {
    return pick(`/trade/claim/status/${claim.id}`);
  }

  // 4. Trade setup incomplete
  if (capabilities.tradeSetupIncomplete) return pick("/trade/join/organisation");

  // 4b. Trade Plan selection required (owner/admin of org with no plan_selected_at)
  const orgNeedingPlan = capabilities.organisationsNeedingPlanSelection?.[0];
  if (orgNeedingPlan) {
    return pick(`/trade/select-plan/${orgNeedingPlan.organisationId}`);
  }

  // 5. Multiple active organisations
  if (capabilities.hasMultipleActiveOrganisations) return pick("/trade/select-organisation");

  const hasTrade = capabilities.tradeMemberships.length > 0;
  const hasConsumer = capabilities.hasConsumerProfile;

  // 6. Dual context — honour last-used
  if (hasTrade && hasConsumer) {
    return pick(lastDomain === "trade" ? "/trade/dashboard" : "/app/home");
  }

  // 7/8. Single-context
  if (hasTrade) return pick("/trade/dashboard");
  if (hasConsumer) return pick("/app/home");

  // 9. Staff-only
  if (capabilities.isStaff) return pick("/admin");

  // 10. Neutral fallback
  return LOOP_SAFE;
}
